<script lang="ts">
import { ref, computed, nextTick } from 'vue';

import { useBreakpointMediaQueries } from '@sg-core/resources/assets/js/composables';
import { uid } from '@sg-core/resources/assets/js/utils';
import { PhxsgCluster } from '@sg-core/src/View/Components/Atoms/Cluster';
import type { PhxPictureProps } from '@sg-core/src/View/Components/Atoms/Picture';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgPicture } from '../../Atoms/Picture';

const DESKTOP_VISIBLE = 5;
const MOBILE_VISIBLE = 4;

export interface VariationsListItem {
  href: string;
  image: Pick<PhxPictureProps, 'image' | 'sources' | 'alt' | 'lazy' | 'type'>;
  actionAttrs?: PhxPictureProps['actionAttrs'];
}
</script>

<script setup lang="ts">
const props = defineProps<{
  items: VariationsListItem[];
  a11y: {
    label?: string;
    showMoreLabel: (count: number) => string;
  };
}>();

const listId = uid('variations-list');
const showHidden = ref(false);
const firstHiddenItemRef = ref<HTMLElement | null>(null);

const revealHidden = async () => {
  showHidden.value = true;
  await nextTick();
  firstHiddenItemRef.value?.querySelector<HTMLElement>('a')?.focus();
};

const viewport = useBreakpointMediaQueries(['md']);
const baseVisibleCount = computed(() => (viewport.value === 'md' ? DESKTOP_VISIBLE : MOBILE_VISIBLE));
const visibleCount = computed(() => {
  const hidden = props.items.length - baseVisibleCount.value;
  return hidden === 1 ? props.items.length : baseVisibleCount.value;
});
const hiddenCount = computed(() => Math.max(0, props.items.length - visibleCount.value));
</script>

<template>
  <nav class="phxsg-variations-list" :aria-label="a11y.label" data-cy="product-variations">
    <PhxsgCluster :id="listId" use="ul" :spacing="2" align="center">
      <li
        v-for="(item, index) in items"
        v-show="index < visibleCount || showHidden"
        :key="`${item.href}-${index}`"
        :ref="index === visibleCount ? (el) => (firstHiddenItemRef = el as HTMLElement | null) : undefined"
        class="phxsg-variations-list__item"
      >
        <PhxsgPicture
          actionable
          :href="item.href"
          :action-attrs="item.actionAttrs"
          v-bind="item.image"
          root-class="phxsg-variations-list__picture"
        />
      </li>

      <li v-if="hiddenCount > 0 && !showHidden" class="phxsg-variations-list__item phxsg-variations-list__item--text">
        <PhxsgButton
          :class="'phxsg-variations-list__button'"
          bare
          type="button"
          :aria-label="a11y.showMoreLabel(hiddenCount)"
          :aria-controls="listId"
          :aria-expanded="showHidden"
          @click="revealHidden"
        >
          +{{ hiddenCount }}
        </PhxsgButton>
      </li>
    </PhxsgCluster>
  </nav>
</template>

<style src="./variations-list.css" />
